import { useCallback, useEffect, useRef, useState } from "react";
import { getUniqueId } from "@jugl-web/utils";
import {
  useRestApiProvider,
  type UsePaginatedRequestHookParams,
  type UsePaginatedRequestHookReturn,
} from "@jugl-web/rest-api";
import { TicketCommentModel } from "@jugl-web/rest-api/tickets/models/TicketComment";

export type UseTicketCommentsParams = UsePaginatedRequestHookParams<{
  entityId: string;
  ticketId: string;
}>;

export type UseTicketCommentsReturn =
  UsePaginatedRequestHookReturn<TicketCommentModel> & {
    totalEntries: number | null;
    addComment: (comment: TicketCommentModel) => void;
    updateComment: (comment: TicketCommentModel) => void;
    removeComment: (commentId: string) => void;
  };

export const useTicketComments = ({
  entityId,
  ticketId,
  pageSize = 20,
  skip,
}: UseTicketCommentsParams): UseTicketCommentsReturn => {
  const { ticketsApi } = useRestApiProvider();
  const requestId = useRef<string>(undefined);

  const [comments, setComments] = useState<TicketCommentModel[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [totalEntries, setTotalEntries] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [fetchTicketComments] = ticketsApi.useLazyGetTicketCommentsQuery();

  const loadPage = useCallback(
    async (page: number) => {
      if (!entityId || !ticketId) return;

      try {
        setIsError(false);
        setIsLoading(true);

        const newRequestId = getUniqueId();
        requestId.current = newRequestId;

        const result = await fetchTicketComments({
          entityId,
          ticketId,
          page,
          page_size: pageSize,
        }).unwrap();

        if (requestId.current !== newRequestId) return;

        if (result) {
          setComments((prev) => {
            if (page === 1) {
              return result.data;
            }

            const existingIds = new Set(prev.map((comment) => comment.id));
            const newComments = result.data.filter(
              (comment) => !existingIds.has(comment.id)
            );

            return [...newComments, ...prev];
          });

          setTotalPages(result.total_pages);
          setTotalEntries(result.total_entries);
          setCurrentPage(page);
        }
      } catch {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [entityId, ticketId, pageSize, fetchTicketComments]
  );

  const loadMore = useCallback(async () => {
    if (isLoading) return;
    if (totalPages !== null && currentPage >= totalPages) return;
    await loadPage(currentPage + 1);
  }, [isLoading, currentPage, totalPages, loadPage]);

  const reset = useCallback(() => {
    setComments([]);
    setCurrentPage(1);
    setTotalPages(null);
    setTotalEntries(null);
    setIsError(false);
    loadPage(1);
  }, [loadPage]);

  const retry = useCallback(async () => {
    const pageToRetry =
      currentPage === 1 && comments.length === 0 ? 1 : currentPage + 1;
    await loadPage(pageToRetry);
  }, [currentPage, comments.length, loadPage]);

  const addComment = useCallback((comment: TicketCommentModel) => {
    setComments((prev) => {
      if (prev.some((item) => item.id === comment.id)) {
        return prev;
      }

      return [...prev, comment];
    });
    setTotalEntries((prev) => (prev ?? 0) + 1);
  }, []);

  const updateComment = useCallback((comment: TicketCommentModel) => {
    setComments((prev) =>
      prev.map((item) =>
        item.id === comment.id
          ? {
              ...comment,
              content: { ...comment.content },
            }
          : item
      )
    );
  }, []);

  const removeComment = useCallback((commentId: string) => {
    setComments((prev) => prev.filter((item) => item.id !== commentId));
    setTotalEntries((prev) => (prev ?? 0) - 1);
  }, []);

  useEffect(() => {
    if (skip) return;
    reset();
  }, [reset, skip, ticketId]);

  const hasMore = totalPages !== null && currentPage < totalPages;
  const isLoadingMore = isLoading && !!comments.length;
  const isLoadingMoreError = isError && comments.length > 0;

  return {
    data: comments,
    totalEntries,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    loadMore,
    reset,
    retry,
    addComment,
    updateComment,
    removeComment,
  };
};
